import { object, string, mixed, boolean, array, number } from "yup";
import axios from "axios";
import { find, some } from "lodash";
import { HomeschoolService } from "../services";
import { useGeoData } from "features/GeoData";

const MAX_FILE_SIZE = 0.5 * (1024 * 1024);
const SUPPORTED_FORMATS = [
  ".pdf",
  "application/pdf",
  ".doc",
  ".docx",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];

export function useHomeschool() {
  const homeschoolService = new HomeschoolService(axios);
    const { countries, activeRegions } = useGeoData(axios);

  async function uploadApplication(data) {
    try {
      return await homeschoolService.postHomeschoolApplicationData(data);
    } catch (error) {
      return {
        success: false,
        errors: [
          "Sorry, we were unable to submit your application at this time, please try again in a few minutes.",
        ],
      };
    }
  }

  const schema = object().shape({
    firstName: string().max(75).required().label("First Name"),
    lastName: string().max(75).required().label("Last Name"),
    email: string().email().required().label("Parent/Guardian Email"),
    localSchoolDistrict: string()
      .max(150)
      .required()
      .label("Local School District"),
    isDataCollectionAgreementAccepted: boolean()
      .oneOf([true], "You must accept the data collection agreement")
      .label("Privacy and Data Usage"),
    streetAddress: string().max(255).required().label("Street Address"),
    city: string().required().max(75).label("City"),
    state: string()
        .test(
          "valid-state",
          ({ label }) => `Must be a valid ${label}`,
          async (value, context) => {
            const { country } = context.from[0].value;
            const countryObj = find(countries, { iso3: country });
            if (countryObj != null) {
              return activeRegions.length > 1
                ? some(activeRegions, { state_code: value })
                : true;
            }
            return false;
          }
        )
      .label("State/Region"),
    country: string()
        .test("valid-country", "Must be a valid Country", (value) => {
          return some(countries, { iso3: value });
        })
      .label("Country"),
    zipCode: string().max(10).required().label("Zip Code"),
    students: array().of(
      object().shape({
        firstName: string().max(75).required().label("First Name"),
        lastName: string().max(75).required().label("Last Name"),
        email: string().email().label("Student Email"),
        gradeLevel: number().required().label("Grade Level"),
        letterOfIntent: mixed()
          .required("You must upload your students Letter of Intent")
          .test("numFiles", "Too many Files", (value) => {
            return value != null && value.length === 1;
          })
          .test("fileSize", "File size is too large", (value) => {
            return (
              value != null &&
              value[0] != null &&
              value[0].size <= MAX_FILE_SIZE
            );
          })
          .test(
            "fileType",
            "We only accept PDF and Word (.docx) formats",
            (value) => {
              return (
                value != null &&
                value[0] != null &&
                SUPPORTED_FORMATS.includes(value[0].type)
              );
            }
          )
          .label("Letter of Intent"),
      })
    ),
  });

  return {
    uploadApplication,
    schema,
    SUPPORTED_FORMATS,
    MAX_FILE_SIZE,
  };
}
